import { Link } from '@backstage/core-components';
import { Box, Tooltip, Typography, makeStyles } from '@material-ui/core';
import { JourneyNode } from './types';
import { teamColor } from './teamColors';

const useStyles = makeStyles(theme => ({
  card: {
    display: 'block',
    padding: theme.spacing(1, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderLeftWidth: 4,
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    transition: 'opacity 120ms, box-shadow 120ms',
    '&:hover': { textDecoration: 'none', boxShadow: theme.shadows[3] },
  },
  dimmed: { opacity: 0.28 },
  notLive: { borderStyle: 'dashed', opacity: 0.6 },
  name: { fontWeight: 600, fontSize: 14, lineHeight: 1.25 },
  meta: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  team: {
    fontSize: 10,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontWeight: 600,
  },
}));

type Props = {
  node: JourneyNode;
  isDimmed: boolean;
  onHover: (id?: string) => void;
};

/** The name, tech and team lines inside a card. */
const NodeBody = ({ node }: { node: JourneyNode }) => {
  const classes = useStyles();
  return (
    <>
      <Typography className={classes.name}>{node.name}</Typography>
      {node.tech && <Box className={classes.meta}>{node.tech}</Box>}
      <Typography
        className={classes.team}
        style={{ color: teamColor(node.team) }}
      >
        {node.team}
        {node.status === 'legacy' && ' · legacy'}
        {node.status === 'not-live' && ' · not live'}
      </Typography>
    </>
  );
};

/** One service, UI or adaptor on the storyboard: coloured by team, linked to its catalog page. */
export const NodeCard = ({ node, isDimmed, onHover }: Props) => {
  const classes = useStyles();
  const className = [
    classes.card,
    isDimmed && classes.dimmed,
    node.status === 'not-live' && classes.notLive,
  ]
    .filter(Boolean)
    .join(' ');
  const shared = {
    className,
    style: { borderLeftColor: teamColor(node.team) },
    onMouseEnter: () => onHover(node.id),
    onMouseLeave: () => onHover(undefined),
    children: <NodeBody node={node} />,
  };

  return (
    <Tooltip title={node.desc ?? ''} placement="right" arrow>
      {node.entityName ? (
        <Link
          to={`/catalog/default/component/${node.entityName}`}
          {...shared}
        />
      ) : (
        <Box {...shared} />
      )}
    </Tooltip>
  );
};
